/**
 * Basemap Slot Manager (ADR-023).
 *
 * Pure functions for managing multiple basemap layers within the `basemaps` slot.
 * Handles CRS compatibility, layer identification, state aggregation, and
 * basemap-specific diagnostics.
 */
import type { Layer } from '@geosoft/viewer-sdk';
import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import { isSyntheticBasemapLayer } from './defaultBasemap';

const WEB_MERCATOR_CODES = new Set([
  'EPSG:3857',
  'EPSG:900913',
  'EPSG:3785',
]);

export function isWebMercatorCrs(crs: string | null | undefined): boolean {
  if (!crs) return true;
  return WEB_MERCATOR_CODES.has(crs.toUpperCase());
}

/**
 * A basemap is compatible with the viewer frame when:
 *  1. The frame is Web Mercator — any basemap works (Web Mercator is the
 *     default basemap rendering frame).
 *  2. Both the basemap and frame are Web Mercator — trivially compatible.
 *  3. The basemap CRS exactly matches the frame CRS — the projected basemap
 *     renders without reprojection.
 *
 * A Web Mercator basemap in a projected frame is always suppressed (no
 * reprojection engine). A projected basemap whose CRS differs from the
 * frame is also suppressed — Giro3D does not reproject raster tiles between
 * arbitrary projected CRSes.
 */
export function isBasemapCrsCompatible(
  basemapCrs: string,
  frameCrs: string,
): boolean {
  if (isWebMercatorCrs(frameCrs)) return true;
  if (isWebMercatorCrs(basemapCrs)) return false;
  return basemapCrs.toUpperCase() === frameCrs.toUpperCase();
}

export type BasemapSource = 'synthetic' | 'converter-imagery' | 'converter-pmtiles' | 'scene-item';

export function isBasemapLayer(
  layer: Pick<Layer, 'id' | 'style'>,
  slotMap?: Map<string, SceneSlot>,
): boolean {
  if (isSyntheticBasemapLayer(layer)) return true;
  if (slotMap?.get(layer.id) === 'basemaps') return true;
  const style = (layer.style ?? {}) as Record<string, unknown>;
  if (style['gtwinSlot'] === 'basemaps') return true;
  if (style['family'] === 'basemap' || style['family'] === 'imagery') return true;
  return false;
}

export function classifyBasemapSource(layer: Pick<Layer, 'id' | 'style' | 'endpoints'>): BasemapSource {
  if (isSyntheticBasemapLayer(layer)) return 'synthetic';
  const style = (layer.style ?? {}) as Record<string, unknown>;
  const endpoints = layer.endpoints ?? {};
  if (endpoints.pmtiles) return 'converter-pmtiles';
  if (style['family'] === 'imagery' || endpoints.cog) return 'converter-imagery';
  return 'scene-item';
}

export interface BasemapEntry {
  layer: Layer;
  source: BasemapSource;
  crsCompatible: boolean;
}

export interface BasemapSlotState {
  entries: BasemapEntry[];
  activeBasemapId: string | undefined;
  suppressedIds: string[];
}

/**
 * Build the aggregate basemap slot state from current layers and slot assignments.
 */
export function buildBasemapSlotState(
  layers: Layer[],
  slotMap: Map<string, SceneSlot>,
  frameCrs: string | undefined,
): BasemapSlotState {
  const entries: BasemapEntry[] = [];
  const suppressedIds: string[] = [];
  let activeBasemapId: string | undefined;

  for (const layer of layers) {
    if (!isBasemapLayer(layer, slotMap)) continue;
    const source = classifyBasemapSource(layer);
    const crsCompatible = frameCrs
      ? isBasemapCrsCompatible(layer.crs ?? 'EPSG:3857', frameCrs)
      : true;
    entries.push({ layer, source, crsCompatible });
    if (!crsCompatible) {
      suppressedIds.push(layer.id);
    } else if (!activeBasemapId) {
      activeBasemapId = layer.id;
    }
  }

  return { entries, activeBasemapId, suppressedIds };
}

export interface BasemapDiagnostic {
  code: string;
  severity: 'warning' | 'error' | 'info';
  title: string;
  detail: string;
  recommendedFix?: string;
  scope: 'producer' | 'viewer' | 'converter';
}

/**
 * Produce diagnostics for a single basemap layer.
 */
export function diagnoseBasemapLayer(
  layer: Pick<Layer, 'id' | 'crs' | 'style'>,
  frameCrs: string | undefined,
): BasemapDiagnostic[] {
  const diagnostics: BasemapDiagnostic[] = [];
  const basemapCrs = layer.crs ?? 'EPSG:3857';

  if (frameCrs && !isBasemapCrsCompatible(basemapCrs, frameCrs)) {
    const isWebMerc = isWebMercatorCrs(basemapCrs);
    diagnostics.push({
      code: 'LP_W_BASEMAP_CRS_INCOMPATIBLE',
      severity: 'warning',
      title: 'Basemap suppressed: CRS incompatible',
      detail: isWebMerc
        ? `This basemap uses ${basemapCrs} but the viewer frame is ` +
          `${frameCrs}. Web Mercator basemaps are hidden in projected frames ` +
          `to prevent misalignment.`
        : `This basemap uses ${basemapCrs} but the viewer frame is ` +
          `${frameCrs}. Basemaps in a different projected CRS are suppressed ` +
          `because Giro3D does not reproject raster tiles between arbitrary ` +
          `projected coordinate systems.`,
      recommendedFix:
        `Provide a basemap in ${frameCrs}, or switch the scene to a Web Mercator frame.`,
      scope: 'producer',
    });
  }

  return diagnostics;
}

/**
 * Collect diagnostics for all basemap entries in the slot.
 */
export function collectBasemapDiagnostics(
  state: BasemapSlotState,
  frameCrs: string | undefined,
): Map<string, BasemapDiagnostic[]> {
  const result = new Map<string, BasemapDiagnostic[]>();
  for (const entry of state.entries) {
    const diags = diagnoseBasemapLayer(entry.layer, frameCrs);
    if (diags.length > 0) {
      result.set(entry.layer.id, diags);
    }
  }
  return result;
}
